<script setup>
// ============================================================================
// BonBoZ: danh sách mẫu trả lời của trang đang trực, bật lên từ thanh nút dưới
// khung soạn tin.
//
// VÌ SAO CẦN NÚT KHI ĐÃ CÓ GÕ "/": gõ "/" chỉ dùng được khi đã biết mã lệnh.
// Người mới vào trực không biết trang này có mẫu nào, và mẫu kèm ảnh thì cái
// tên `/seo-ro` không cho biết nó gửi mấy tấm gì. Nút này là chỗ để XEM; gõ
// "/" vẫn là đường nhanh cho người thuộc mã.
//
// Bộ lọc theo trang dùng lại `locMauTheoTrang` — cùng một hàm với danh sách gõ
// "/" (CannedResponse.vue). Hai chỗ lệch nhau thì người trực gửi nhầm mẫu của
// trang khác cho khách.
//
// Ảnh của mẫu chèn thẳng vào khay chờ, KHÔNG gửi: xem `chenAnhCuaMau`.
//
// GIAO DIỆN BẢNG KIỂU PANCAKE: STT · ký tự tắt · chủ đề · tin nhắn, mỗi mẫu
// đúng một dòng. Người trực chuyển từ Pancake sang đã quen đọc theo cột, và một
// dòng/mẫu thì thấy được ~10 mẫu cùng lúc thay vì 4 như bản thẻ cũ. Chủ đề là
// viên màu ĐẶC chữ trắng — ở bảng dày đặc, màu nhạt không đủ để mắt bắt.
// ============================================================================
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { loadPageConfigs, locMauTheoTrang } from 'dashboard/helper/pageConfig';
import { chenAnhCuaMau } from 'dashboard/helper/bonboz/khoAnh';
import { datConTro } from 'dashboard/helper/thietBi';
import {
  noiTin,
  thayBien,
  nguCanhChen,
} from 'dashboard/helper/bonboz/mauNhieuTin';
import {
  dsChuDe,
  napChuDe,
  chuDeTheoId,
} from 'dashboard/helper/bonboz/chuDeMau';

const props = defineProps({
  // Ham cua ReplyBox — anh cua mau di vao khay y het tep tu chon.
  onFileUpload: { type: Function, required: true },
});
const emit = defineEmits(['chen']);
const hien = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();
const dsMau = useMapGetter('getCannedResponses');
const hoiThoai = useMapGetter('getSelectedChat');
const nhanVien = useMapGetter('getCurrentUser');

const hopRef = ref(null);
const tuKhoa = ref('');
const chuDeLoc = ref(null); // null = moi chu de
const hienLoc = ref(false); // menu loc mo tu icon pheu o cot Chu de
const dangChen = ref(false);
const oTimRef = ref(null);
const dsRef = ref(null);
// Dong dang chon bang ban phim (↑↓ Enter) — chuot re vao cung doi dong nay.
const dangChon = ref(0);

onClickOutside(hopRef, () => {
  hien.value = false;
});

onMounted(() => {
  store.dispatch('getCannedResponse', {});
  loadPageConfigs();
  napChuDe();
  // Mo bang la go duoc ngay: tay dang o ban phim, dung bat cam chuot.
  // CHI tren may co chuot. Tren dien thoai, dat con tro vao day se keo ban phim
  // ao troi len che mat dung cai bang vua mo (bao loi 22/09/2026).
  datConTro(oTimRef.value);
});

// Xung ho doi ngay trong bang: nguoi truc doc dung cau khach se nhan.
const nguCanh = computed(() =>
  nguCanhChen(
    store.getters['contacts/getContact'](hoiThoai.value?.meta?.sender?.id),
    nhanVien.value
  )
);

const danhSach = computed(() => {
  const cuaTrang = locMauTheoTrang(dsMau.value, hoiThoai.value?.inbox_id);
  const k = tuKhoa.value.trim().toLowerCase();
  return cuaTrang
    .map(m => ({
      m,
      tin: [m.content || '', ...(m.tin_them || [])]
        .map(x => thayBien(x, nguCanh.value))
        .filter(x => x.trim()),
    }))
    .filter(
      ({ m }) => !chuDeLoc.value || Number(m.chu_de_id) === chuDeLoc.value
    )
    .filter(
      ({ m, tin }) =>
        !k ||
        m.short_code?.toLowerCase().includes(k) ||
        tin.some(x => x.toLowerCase().includes(k))
    )
    .map(({ m, tin }) => ({
      id: m.id,
      maLenh: m.short_code,
      // Cac tin ngan bang vach; ReplyBox cat tai vach va gui tung bong bong.
      noiDung: noiTin(tin),
      // Tooltip: vach ngan giua cac tin de thay ro day la tin rieng.
      xemDu: tin.join('\n───\n'),
      // Mot dong trong bang: bo xuong dong, cat bang CSS.
      motDong: tin.join(' · ').replace(/\s+/g, ' '),
      ban: m,
      chuDe: chuDeTheoId(m.chu_de_id),
      // Server tra kem URL anh — anh o kho trang nao cung hien duoc.
      anh: m.anh || [],
      soAnh: (m.anh_ids || []).length,
      soTin: tin.length,
    }));
});

// Loc lai thi ve dong dau, khong de con tro tro vao dong khong con ton tai.
watch([tuKhoa, chuDeLoc], () => {
  dangChon.value = 0;
});

// Chi hien chu de co mau trong trang nay: trang tuyen dung khong can thay chu
// de "Bang gia" cua ben tham my.
const chuDeCoMau = computed(() => {
  const co = new Set(
    locMauTheoTrang(dsMau.value, hoiThoai.value?.inbox_id)
      .map(m => Number(m.chu_de_id))
      .filter(Boolean)
  );
  return dsChuDe.value.filter(cd => co.has(Number(cd.id)));
});

const locTheo = id => {
  chuDeLoc.value = id;
  hienLoc.value = false;
};
const chuDeDangLoc = computed(() => chuDeTheoId(chuDeLoc.value));

const diChuyen = buoc => {
  const n = danhSach.value.length;
  if (!n) return;
  dangChon.value = (dangChon.value + buoc + n) % n;
  nextTick(() => {
    dsRef.value
      ?.querySelector('[data-dang-chon="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  });
};

const chon = async mau => {
  if (dangChen.value) return;
  emit('chen', mau.noiDung);
  hien.value = false;
  if (!mau.soAnh) return;
  dangChen.value = true;
  try {
    const thieu = await chenAnhCuaMau(mau.ban, props.onFileUpload);
    if (thieu) useAlert(t('BONBOZ.MAU_ANH.THIEU_ANH'));
  } catch {
    useAlert(t('BONBOZ.KHO_ANH.LOI_CHON'));
  } finally {
    dangChen.value = false;
  }
};

const chonDangChon = () => {
  const mau = danhSach.value[dangChon.value];
  if (mau) chon(mau);
};
</script>

<template>
  <!-- `.stop` tren MOI phim, KHONG duoc bo: ReplyBox bat Enter o cap `document`
       de GUI TIN. Chi `.prevent` thi Enter chon mau van lan len document va gui
       luon noi dung dang co trong o soan cho khach — da xay ra that 17/09/2026,
       mot tin nhap bi gui sang Zalo khi thu bang nay. -->
  <div
    ref="hopRef"
    class="bonboz-hop-mau absolute z-50 flex flex-col border bottom-12 ltr:-right-2 rtl:-left-2 w-[min(42rem,calc(100vw-2rem))] max-h-[min(32rem,calc(100dvh-8rem))] rounded-2xl bg-n-solid-1 border-n-weak shadow-[0_2px_6px_rgba(0,0,0,.06),0_24px_48px_-12px_rgba(0,0,0,.28)]"
    @keydown.down.prevent.stop="diChuyen(1)"
    @keydown.up.prevent.stop="diChuyen(-1)"
    @keydown.enter.prevent.stop="chonDangChon"
    @keydown.esc.prevent.stop="hien = false"
  >
    <div class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak">
      <div class="flex items-center flex-1 min-w-0 gap-2">
        <span class="i-lucide-search size-4 shrink-0 text-n-slate-10" />
        <input
          ref="oTimRef"
          v-model="tuKhoa"
          type="text"
          class="bonboz-o-tim !mb-0 !h-7 !border-0 !bg-transparent !p-0 w-full text-sm !shadow-none !outline-none focus:!ring-0"
          :placeholder="$t('BONBOZ.MAU_ANH.TIM')"
        />
      </div>
      <span class="text-sm font-semibold text-n-slate-12 shrink-0">
        {{ $t('BONBOZ.BANG_MAU.TIEU_DE') }}
      </span>
      <router-link
        v-tooltip.top="$t('BONBOZ.BANG_MAU.SUA')"
        :to="{ name: 'canned_list' }"
        class="grid rounded-md size-7 place-items-center text-n-slate-11 hover:bg-n-alpha-2 ltr:border-l rtl:border-r border-n-weak"
        @click="hien = false"
      >
        <span class="i-lucide-pencil size-4" />
      </router-link>
    </div>

    <!-- LUOI CHUNG + SUBGRID: tieu de va moi dong dung chung mot bo cot.
         Cot chu de la `max-content` nen rong dung bang vien chu de DAI NHAT,
         va vien nao cung `w-full` -> moi vien bang nhau, chu can giua. Moi dong
         la mot grid rieng thi khong the lam cac vien bang nhau ma khong do chu
         bang JS. -->
    <div
      ref="dsRef"
      class="grid content-start overflow-y-auto grid-cols-[2rem_minmax(6rem,10rem)_max-content_minmax(0,1fr)]"
    >
      <div
        class="sticky top-0 z-[1] grid items-center col-span-full grid-cols-subgrid gap-x-3 px-4 py-2 text-xs font-semibold border-b bg-n-solid-2 text-n-slate-12 border-n-weak"
      >
        <span />
        <span>{{ $t('BONBOZ.BANG_MAU.KY_TU_TAT') }}</span>
        <div class="relative">
          <button
            type="button"
            class="flex items-center justify-between w-full gap-2 text-start"
            @click="hienLoc = !hienLoc"
          >
            <span class="truncate max-w-[10rem]">
              {{
                chuDeDangLoc ? chuDeDangLoc.ten : $t('BONBOZ.BANG_MAU.CHU_DE')
              }}
            </span>
            <span
              class="i-lucide-filter size-3.5 shrink-0"
              :class="chuDeLoc ? 'text-n-brand' : 'text-n-slate-10'"
            />
          </button>
          <div
            v-if="hienLoc"
            class="absolute z-10 flex flex-col p-1 mt-2 overflow-y-auto font-normal border rounded-lg shadow-lg top-full ltr:left-0 rtl:right-0 w-44 max-h-64 bg-n-solid-1 border-n-weak"
          >
            <button
              type="button"
              class="px-2 py-1.5 text-sm rounded-md text-start hover:bg-n-alpha-2"
              :class="{ 'font-semibold': chuDeLoc === null }"
              @click="locTheo(null)"
            >
              {{ $t('BONBOZ.CHU_DE.TAT_CA') }}
            </button>
            <button
              v-for="cd in chuDeCoMau"
              :key="cd.id"
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md text-start hover:bg-n-alpha-2"
              :class="{ 'font-semibold': chuDeLoc === Number(cd.id) }"
              @click="locTheo(Number(cd.id))"
            >
              <span
                class="rounded-full size-2.5 shrink-0"
                :style="{ backgroundColor: cd.mau }"
              />
              <span class="truncate">{{ cd.ten }}</span>
            </button>
          </div>
        </div>
        <span>{{ $t('BONBOZ.BANG_MAU.TIN_NHAN') }}</span>
      </div>

      <div
        v-if="!danhSach.length"
        class="px-4 py-8 text-sm text-center col-span-full text-n-slate-11"
      >
        {{ $t('BONBOZ.MAU_ANH.TRONG') }}
      </div>
      <!-- Ke soc xen ke thay duong ke; dong dang chon co nen + vach trai.
           Re chuot 0.4s hien du noi dung, khoi phai chen thu roi xoa. -->
      <button
        v-for="(mau, i) in danhSach"
        :key="mau.id"
        v-tooltip.left="{
          content: mau.xemDu,
          delay: { show: 400, hide: 0 },
          popperClass: 'bonboz-noi-dung-mau',
        }"
        type="button"
        :data-dang-chon="i === dangChon"
        class="grid items-center col-span-full grid-cols-subgrid gap-x-3 px-4 min-h-[34px] py-1 text-sm text-start"
        :class="
          i === dangChon
            ? 'bg-n-brand/10 shadow-[inset_3px_0_0_rgb(var(--brand))]'
            : 'even:bg-n-alpha-1 hover:bg-n-alpha-2'
        "
        @mouseenter="dangChon = i"
        @click="chon(mau)"
      >
        <span class="text-n-slate-11">{{ `${i + 1}.` }}</span>
        <!-- Hien dung thu nguoi truc go: /ma. -->
        <span class="font-mono text-[13px] truncate text-n-slate-12">
          {{ `/${mau.maLenh}` }}
        </span>
        <span
          v-if="mau.chuDe"
          class="block w-full max-w-[10rem] px-2.5 py-0.5 text-xs font-semibold text-center text-white truncate rounded-full"
          :style="{ backgroundColor: mau.chuDe.mau }"
        >
          {{ mau.chuDe.ten }}
        </span>
        <span v-else />
        <span class="flex items-center min-w-0 gap-2">
          <span
            v-if="mau.soAnh"
            class="px-1.5 py-0.5 text-xs font-semibold text-white rounded-md shrink-0 bg-n-slate-9"
          >
            {{ `+${mau.soAnh}` }}
          </span>
          <span
            v-if="mau.soTin > 1"
            class="px-1 text-[11px] border rounded shrink-0 border-n-weak text-n-slate-11"
          >
            {{ $t('BONBOZ.BANG_MAU.SO_TIN', { so: mau.soTin }) }}
          </span>
          <span class="truncate text-n-slate-12">{{ mau.motDong }}</span>
        </span>
      </button>
    </div>

    <span
      class="absolute rotate-45 border-b border-r -bottom-1.5 ltr:right-4 rtl:left-4 size-3 bg-n-solid-1 border-n-weak"
    />
  </div>
</template>

<!-- KHONG scoped: tooltip cua floating-vue duoc dua ra ngoai component (gan vao
     body) nen style scoped khong cham toi duoc.
     Mac dinh tooltip mot dong, bo xuong dong -> mau 3 tin thanh mot dong dai che
     mat dong ben duoi. `pre-line` giu dung xuong dong nhu khach se nhan. -->
<style lang="scss">
.v-popper--theme-tooltip.bonboz-noi-dung-mau .v-popper__inner {
  max-width: 22rem;
  white-space: pre-line;
  line-height: 1.5;
  font-size: 0.8125rem;
  padding: 8px 10px !important;
}

// BonBoZ: iOS phong to ca trang khi con tro vao o nhap co co chu duoi 16px.
// Luat chung trong `_bonboz-theme.scss` dung bo chon `input:not(...)`, do uu
// tien (0,3,1) — thua `.text-sm` (0,1,0) ve ly thuyet, nhung o nay VAN bi phong
// to that (bao loi 23/09/2026). Lap ten lop cho chac, khong doan nua.
@media (pointer: coarse) {
  .bonboz-o-tim.bonboz-o-tim {
    font-size: 16px;
  }
}

// BonBoZ: tren THIET BI CAM UNG, bo hoan toan cach neo theo nut bam.
//
// Popup `absolute` trong mot `div.relative` boc rieng cai nut, va neo bang
// `-right-2` — tuc la mep phai popup bam theo mep phai NUT, roi trai rong sang
// TRAI. Tren man rong thi vua, nhung tren dien thoai popup rong gan het man
// hinh nen nua ben kia roi han ra ngoai (bao loi 22/09/2026, mat gan mot nua).
//
// Day khong phai loi kich thuoc — co lam popup nho lai cung khong cuu duoc, vi
// diem neo phu thuoc vi tri cai nut trong thanh cong cu. Popup emoji khong dinh
// vi lieu nay: no khong dat `left`/`right` nao ca nen trai sang PHAI tu nut, va
// nut lai nam ben trai, nen vua.
//
// `fixed` + `inset-inline` ghim thang vao man hinh, khong con phu thuoc nut nam
// dau. `dvh` de ban phim bat len thi popup co lai theo.
@media (pointer: coarse) {
  // Lap ten lop de thang cac lop Tailwind dat inline tren the.
  .bonboz-hop-mau.bonboz-hop-mau {
    position: fixed;
    inset-inline: 0.5rem;
    bottom: 4.5rem;
    width: auto;
    max-width: none;
    max-height: calc(100dvh - 9rem);
  }
}
</style>
